import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowRight, TrendingUp, Clock, DollarSign, Server } from "lucide-react";

const caseStudies = [
  {
    industry: "FinTech",
    title: "Zero-downtime migration from bare metal to AWS",
    client: "FinStack",
    problem: "Aging on-prem infrastructure caused daily outages, month-long release cycles, and rising OpEx. Peak traffic scaling was manual and painful.",
    solution: "Phased migration to AWS with EKS-hosted microservices, blue-green deployments, Prometheus + Grafana observability, and Karpenter autoscaling.",
    outcomes: [
      { icon: Clock, label: "Downtime during migration", value: "0 hrs" },
      { icon: DollarSign, label: "Infrastructure cost reduction", value: "-35%" },
      { icon: TrendingUp, label: "Response time improvement", value: "3×" },
      { icon: Server, label: "Deploy frequency", value: "50/wk" },
    ],
  },
  {
    industry: "Healthcare",
    title: "HIPAA-compliant patient portal, launched in 8 weeks",
    client: "HealthBridge",
    problem: "Needed to launch a patient portal but had no cloud team and non-negotiable HIPAA requirements.",
    solution: "HIPAA-ready AWS landing zone from scratch: KMS-based encryption, VPC segmentation, IAM Identity Center, CloudTrail + GuardDuty, 24/7 CloudOps.",
    outcomes: [
      { icon: Clock, label: "Time to production", value: "8 wks" },
      { icon: DollarSign, label: "HIPAA audit result", value: "Passed" },
      { icon: TrendingUp, label: "Uptime since launch", value: "99.99%" },
      { icon: Server, label: "Patient records secured", value: "100K+" },
    ],
  },
  {
    industry: "SaaS · Analytics",
    title: "DevOps transformation: monthly to daily releases",
    client: "DataPulse",
    problem: "Engineers spent 40% of their time on manual deploys, env drift, and firefighting production incidents.",
    solution: "GitOps with ArgoCD, Terraform-managed infra, hermetic CI, ephemeral preview envs per PR, and SLO-driven alerting.",
    outcomes: [
      { icon: Clock, label: "Deploy time", value: "5 min" },
      { icon: DollarSign, label: "Eng time reclaimed", value: "+40%" },
      { icon: TrendingUp, label: "Release cadence", value: "Daily" },
      { icon: Server, label: "Prod incidents", value: "-80%" },
    ],
  },
];

const CaseStudies = () => {
  return (
    <Layout>
      <Helmet>
        <title>Case Studies — Cloudease</title>
        <meta name="description" content="Real results from real engagements. See how Cloudease has helped fintech, healthcare, and SaaS teams transform their cloud infrastructure." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-aurora animate-aurora" aria-hidden />
        <div className="container relative mx-auto px-4 lg:px-8 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-5">Customers</p>
            <h1 className="text-5xl md:text-7xl font-heading font-semibold tracking-tight mb-6">
              Outcomes, <span className="text-gradient">not slideware.</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Three engagements. Zero fluff. What we built, why it mattered, and what it moved.
            </p>
          </motion.div>
        </div>
      </section>

      {caseStudies.map((cs, idx) => (
        <section key={cs.title} className={`py-24 border-b border-border/60 ${idx % 2 === 1 ? "bg-secondary/20" : ""}`}>
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full glass text-[10px] font-mono uppercase tracking-widest">
                  {cs.industry}
                </span>
                <span className="text-xs font-mono text-muted-foreground">Case · 0{idx + 1}</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-heading font-semibold tracking-tight mb-10 max-w-3xl">
                {cs.title}
              </h2>

              <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-4 p-6 rounded-2xl glass">
                  <h3 className="text-xs font-mono uppercase tracking-widest text-destructive mb-3">Problem</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{cs.problem}</p>
                </div>
                <div className="lg:col-span-5 p-6 rounded-2xl glass">
                  <h3 className="text-xs font-mono uppercase tracking-widest text-primary mb-3">Solution</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{cs.solution}</p>
                </div>
                <div className="lg:col-span-3 p-6 rounded-2xl bg-brand text-white">
                  <h3 className="text-xs font-mono uppercase tracking-widest text-white/70 mb-4">Outcomes</h3>
                  <div className="space-y-4">
                    {cs.outcomes.map((o) => (
                      <div key={o.label}>
                        <div className="text-2xl font-heading font-semibold">{o.value}</div>
                        <div className="text-[11px] font-mono uppercase tracking-widest text-white/70">{o.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh opacity-40 animate-aurora" aria-hidden />
        <div className="container relative mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight mb-5">
            Your story is next.
          </h2>
          <p className="text-muted-foreground mb-8">Book a call and we'll scope a similar transformation for your team.</p>
          <Link to="/contact">
            <Button variant="gradient" size="xl" className="rounded-full">
              Start your project <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default CaseStudies;
